export const calculateZones = (data) => {

  const { method, sport = 'running', age, restingHR, maxHR, lthr, vt1, vt2 } = data;

  

  let zones = [];

  let methodInfo = {};

  let calculatedMaxHR = maxHR;



  switch (method) {

    case 'lab':

      // Lab Test: 3-Zone Model based on VT1 and VT2

      if (!vt1 || !vt2) return null;

      

      zones = [

        { 

          number: '1', 

          name: 'Moderate / Light', 

          min: 'Rest', 

          max: vt1 - 1, 

          desc: 'Below VT1: Primary fuel is fat. Comfortable conversation possible (Talk Test).',

          domain: 'Moderate Intensity Domain'

        },

        { 

          number: '2', 

          name: 'Heavy / Moderate', 

          min: vt1, 

          max: vt2 - 1, 

          desc: 'Between VT1 & VT2: Lactate steady state and aerobic power.',

          domain: 'Heavy Intensity Domain'

        },

        { 

          number: '3', 

          name: 'Severe / High', 

          min: vt2, 

          max: 'Max', 

          desc: 'Above VT2: Lactate accumulation and anaerobic capacity.',

          domain: 'Severe Intensity Domain'

        }

      ];



      methodInfo = {

        name: 'Lab Test (3-Zone Model)',

        formula: '3-Zone Physiological Model',

        zoneCalculation: 'Zones defined by Ventilatory Thresholds (VT1 & VT2)',

        accuracy: 'Gold Standard 🥇',

        description: 'Physiological 3-zone model separating intensity into Moderate, Heavy, and Severe domains based on metabolic breakpoints.',

        audience: 'Professional or serious athletes using physiological testing.'

      };

      break;



    case 'friel':

      // Joe Friel LTHR Zones (7-Zone Model with Sport Specificity)

      if (!lthr) return null;

      

      const isRunning = sport === 'running';

      

      // Define percentages based on sport

      const ranges = isRunning ? [

        { min: 0, max: 0.85, label: '< 85%' },           // Z1

        { min: 0.85, max: 0.89, label: '85% - 89%' },    // Z2

        { min: 0.90, max: 0.94, label: '90% - 94%' },    // Z3

        { min: 0.95, max: 0.99, label: '95% - 99%' },    // Z4

        { min: 1.00, max: 1.02, label: '100% - 102%' },  // Z5a

        { min: 1.03, max: 1.06, label: '103% - 106%' },  // Z5b

        { min: 1.06, max: null, label: '> 106%' }        // Z5c

      ] : [

        { min: 0, max: 0.81, label: '< 81%' },           // Z1

        { min: 0.81, max: 0.89, label: '81% - 89%' },    // Z2

        { min: 0.90, max: 0.93, label: '90% - 93%' },    // Z3

        { min: 0.95, max: 0.99, label: '95% - 99%' },    // Z4

        { min: 1.00, max: 1.02, label: '100% - 102%' },  // Z5a

        { min: 1.03, max: 1.06, label: '103% - 106%' },  // Z5b

        { min: 1.06, max: null, label: '> 106%' }        // Z5c

      ];



      // Helper to calc value. For range starts, we usually use ceil or round. 

      // Friel tables are often explicit. 

      // We'll use strict boundaries: 

      // Z1 Max = 0.85 * LTHR (minus 1 implicitly by start of next)

      // But standard integer zones usually go: 130-140, 141-150.

      

      const getVal = (pct) => Math.round(lthr * pct);



      zones = [

        { 

          number: '1', 

          name: 'Recovery', 

          min: 0, 

          max: getVal(ranges[0].max) - 1, 

          desc: 'Easy recovery, warming up, cool down.', 

          rangeLabel: ranges[0].label 

        },

        { 

          number: '2', 

          name: 'Aerobic', 

          min: getVal(ranges[1].min), 

          max: getVal(ranges[1].max), 

          desc: 'Endurance, long slow distance, base building.', 

          rangeLabel: ranges[1].label 

        },

        { 

          number: '3', 

          name: 'Tempo', 

          min: getVal(ranges[2].min), 

          max: getVal(ranges[2].max), 

          desc: 'Muscular endurance, sweet spot training.', 

          rangeLabel: ranges[2].label 

        },

        { 

          number: '4', 

          name: 'Subthreshold', 

          min: getVal(ranges[3].min), 

          max: getVal(ranges[3].max), 

          desc: 'High intensity, just below lactate threshold.', 

          rangeLabel: ranges[3].label 

        },

        { 

          number: '5a', 

          name: 'Superthreshold', 

          min: getVal(ranges[4].min), 

          max: getVal(ranges[4].max), 

          desc: 'Above threshold, VO2max development.', 

          rangeLabel: ranges[4].label 

        },

        { 

          number: '5b', 

          name: 'Aerobic Capacity', 

          min: getVal(ranges[5].min), 

          max: getVal(ranges[5].max), 

          desc: 'Anaerobic endurance, very hard efforts.', 

          rangeLabel: ranges[5].label 

        },

        { 

          number: '5c', 

          name: 'Anaerobic Capacity', 

          min: getVal(ranges[6].min), 

          max: 'Max', 

          desc: 'Maximal effort, sprints, neuromuscular power.', 

          rangeLabel: ranges[6].label 

        }

      ];



      methodInfo = {

        name: `Joe Friel LTHR (${isRunning ? 'Running' : 'Cycling'})`,

        formula: isRunning ? 'Running Zones (7-Zone Model)' : 'Cycling Zones (7-Zone Model)',

        zoneCalculation: `Target HR = % of LTHR (Specific to ${isRunning ? 'Running' : 'Cycling'})`,

        accuracy: 'Silver Standard 🥈',

        description: 'Advanced 7-zone system specific to your sport, allowing for precise high-intensity interval training prescription.',

        audience: 'Competitive runners and cyclists requiring granular training zones.'

      };

      break;



    case 'karvonen':

      // Karvonen (HRR)

      if (!maxHR || !restingHR) return null;

      const hrr = maxHR - restingHR;



      zones = [

        { number: '1', name: 'Recovery', range: [0.50, 0.60], desc: 'Very light intensity' },

        { number: '2', name: 'Aerobic', range: [0.60, 0.70], desc: 'Endurance building' },

        { number: '3', name: 'Tempo', range: [0.70, 0.80], desc: 'Aerobic capacity' },

        { number: '4', name: 'Threshold', range: [0.80, 0.90], desc: 'Lactate tolerance' },

        { number: '5', name: 'Maximum', range: [0.90, 1.00], desc: 'Peak performance' }

      ].map(z => ({

        ...z,

        min: Math.round((hrr * z.range[0]) + restingHR),

        max: Math.round((hrr * z.range[1]) + restingHR)

      }));



      methodInfo = {

        name: 'Karvonen (Heart Rate Reserve)',

        formula: '((Max HR - Resting HR) × %) + Resting HR',

        zoneCalculation: 'Target HR = ((Max HR − Resting HR) × %) + Resting HR',

        accuracy: 'Bronze Standard 🥉',

        description: 'Accounts for individual fitness levels by incorporating resting heart rate.',

        audience: 'Intermediate runners looking for more personalized zones than age-based formulas.'

      };

      break;



    case 'tanaka':

      // Tanaka Formula

      if (!age) return null;

      calculatedMaxHR = Math.round(208 - (0.7 * age));



      zones = [

        { number: '1', name: 'Recovery', range: [0.50, 0.60], desc: 'Warm-up / Recovery' },

        { number: '2', name: 'Aerobic', range: [0.60, 0.70], desc: 'Fat burning / Base' },

        { number: '3', name: 'Tempo', range: [0.70, 0.80], desc: 'Aerobic fitness' },

        { number: '4', name: 'Threshold', range: [0.80, 0.90], desc: 'Performance capacity' },

        { number: '5', name: 'Maximum', range: [0.90, 1.00], desc: 'Max effort' }

      ].map(z => ({

        ...z,

        min: Math.round(calculatedMaxHR * z.range[0]),

        max: Math.round(calculatedMaxHR * z.range[1])

      }));



      methodInfo = {

        name: 'Tanaka Formula',

        formula: 'Max HR = 208 - (0.7 × Age)',

        zoneCalculation: 'Target HR = % of Max HR (derived from 208 - 0.7 × Age)',

        accuracy: 'Base Accuracy',

        description: 'A scientifically derived regression equation that is generally more accurate than 220-Age for a wider population.',

        audience: 'General fitness enthusiasts and older adults.'

      };

      break;



    case 'fox':

      // Fox & Haskell (220 - Age)

      if (!age) return null;

      calculatedMaxHR = 220 - age;



      zones = [

        { number: '1', name: 'Recovery', range: [0.50, 0.60], desc: 'Warm-up / Recovery' },

        { number: '2', name: 'Aerobic', range: [0.60, 0.70], desc: 'Fat burning / Base' },

        { number: '3', name: 'Tempo', range: [0.70, 0.80], desc: 'Aerobic fitness' },

        { number: '4', name: 'Threshold', range: [0.80, 0.90], desc: 'Performance capacity' },

        { number: '5', name: 'Maximum', range: [0.90, 1.00], desc: 'Max effort' }

      ].map(z => ({

        ...z,

        min: Math.round(calculatedMaxHR * z.range[0]),

        max: Math.round(calculatedMaxHR * z.range[1])

      }));



      methodInfo = {

        name: 'Fox & Haskell',

        formula: 'Max HR = 220 - Age',

        zoneCalculation: 'Target HR = % of Max HR (derived from 220 - Age)',

        accuracy: 'Low / Discouraged ⛔',

        description: 'The oldest and simplest formula. Often inaccurate, with a standard deviation of ~10-12 bpm.',

        audience: 'Rough estimation only. Not recommended for serious training.'

      };

      break;

      

    default:

      return null;

  }



  return {

    zones,

    info: methodInfo,

    calculatedMaxHR

  };

};

